/**
 * KioskMemberSignup - New member signup form
 * Uses custom keypads for portrait kiosk mode
 */
import { useState } from 'react';
import { Crown, ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKiosk, COUNTRY_CODES } from './KioskContext';
import { NumericKeypad, AlphanumericKeypad } from './KioskKeypad';

export default function KioskMemberSignup() {
  const {
    setView, isAccessible, buttonSizeClass, iconClass,
    signupForm, setSignupForm, signupLoading, handleSignup
  } = useKiosk();

  // Track which field is active for the keypad
  const [activeField, setActiveField] = useState(null);

  // Handle keypad input for different fields
  const handleKeyPress = (key) => {
    if (!activeField) return;
    
    const currentValue = signupForm[activeField] || '';
    
    if (key === 'backspace') {
      setSignupForm(prev => ({ ...prev, [activeField]: currentValue.slice(0, -1) }));
    } else if (key === 'clear') {
      setSignupForm(prev => ({ ...prev, [activeField]: '' }));
    } else {
      // For phone, only allow digits
      if (activeField === 'phone') {
        if (/^\d$/.test(key) && currentValue.length < 15) {
          setSignupForm(prev => ({ ...prev, [activeField]: currentValue + key }));
        }
      } else {
        // For name/email fields, allow any character
        setSignupForm(prev => ({ ...prev, [activeField]: currentValue + key }));
      }
    }
  };

  const handleDone = () => {
    setActiveField(null);
  };

  // Check if form is valid
  const isFormValid = signupForm.first_name && signupForm.phone;

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6 overflow-auto" data-testid="kiosk-member-signup">
      <Button
        variant="ghost"
        onClick={() => setView('welcome')}
        className={`mb-4 text-white ${buttonSizeClass}`}
      >
        <ChevronLeft className={iconClass} />
        <span className={`ml-2 ${isAccessible ? 'text-xl' : ''}`}>Back</span>
      </Button>

      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <Crown className={`${isAccessible ? 'w-16 h-16' : 'w-12 h-12'} mx-auto text-amber-400 mb-3`} />
          <h1 className={`${isAccessible ? 'text-4xl' : 'text-3xl'} font-bold mb-1`}>Join Rewards</h1>
          <p className={`${isAccessible ? 'text-xl' : 'text-lg'} text-gray-400`}>
            Start earning points today!
          </p>
        </div>

        <div className="space-y-4">
          {/* First Name Field */}
          <div>
            <label className={`block ${isAccessible ? 'text-lg' : 'text-base'} text-gray-400 mb-1`}>First Name *</label>
            <div
              onClick={() => setActiveField('first_name')}
              className={`w-full ${isAccessible ? 'py-5 text-2xl' : 'py-4 text-xl'} px-4 bg-gray-900 border-2 ${activeField === 'first_name' ? 'border-primary' : 'border-gray-700'} rounded-xl text-white cursor-pointer`}
              data-testid="kiosk-signup-firstname"
            >
              {signupForm.first_name || <span className="text-gray-500">Tap to enter</span>}
            </div>
          </div>

          {/* Last Name Field */}
          <div>
            <label className={`block ${isAccessible ? 'text-lg' : 'text-base'} text-gray-400 mb-1`}>Last Name</label>
            <div
              onClick={() => setActiveField('last_name')}
              className={`w-full ${isAccessible ? 'py-5 text-2xl' : 'py-4 text-xl'} px-4 bg-gray-900 border-2 ${activeField === 'last_name' ? 'border-primary' : 'border-gray-700'} rounded-xl text-white cursor-pointer`}
              data-testid="kiosk-signup-lastname"
            >
              {signupForm.last_name || <span className="text-gray-500">Tap to enter (optional)</span>}
            </div>
          </div>

          {/* Phone with Country Code */}
          <div>
            <label className={`block ${isAccessible ? 'text-lg' : 'text-base'} text-gray-400 mb-1`}>Phone Number *</label>
            <div className="flex gap-2">
              <select
                value={signupForm.country_code}
                onChange={(e) => setSignupForm(prev => ({ ...prev, country_code: e.target.value }))}
                className={`${isAccessible ? 'py-5 text-xl' : 'py-4 text-lg'} px-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white`}
                data-testid="kiosk-signup-country-code"
              >
                {COUNTRY_CODES.map(cc => (
                  <option key={cc.code} value={cc.code}>
                    {cc.flag} {cc.code}
                  </option>
                ))}
              </select>
              <div
                onClick={() => setActiveField('phone')}
                className={`flex-1 ${isAccessible ? 'py-5 text-2xl' : 'py-4 text-xl'} px-4 bg-gray-900 border-2 ${activeField === 'phone' ? 'border-primary' : 'border-gray-700'} rounded-xl text-white cursor-pointer`}
                data-testid="kiosk-signup-phone"
              >
                {signupForm.phone || <span className="text-gray-500">Tap to enter</span>}
              </div>
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label className={`block ${isAccessible ? 'text-lg' : 'text-base'} text-gray-400 mb-1`}>Email (optional)</label>
            <div
              onClick={() => setActiveField('email')}
              className={`w-full ${isAccessible ? 'py-5 text-2xl' : 'py-4 text-xl'} px-4 bg-gray-900 border-2 ${activeField === 'email' ? 'border-primary' : 'border-gray-700'} rounded-xl text-white cursor-pointer`}
              data-testid="kiosk-signup-email"
            >
              {signupForm.email || <span className="text-gray-500">Tap to enter (optional)</span>}
            </div>
          </div>

          {/* Custom Keypad */}
          {activeField && (
            <div className="mt-4">
              {activeField === 'phone' ? (
                <NumericKeypad
                  onKeyPress={handleKeyPress}
                  onDone={handleDone}
                  doneText="Done"
                  isAccessible={isAccessible}
                  disabled={false}
                />
              ) : (
                <AlphanumericKeypad
                  onKeyPress={handleKeyPress}
                  onDone={handleDone}
                  doneText="Done"
                  isAccessible={isAccessible}
                  disabled={false}
                />
              )}
            </div>
          )}

          {/* Submit Button - only show when no keypad is active */}
          {!activeField && (
            <>
              <button
                onClick={handleSignup}
                disabled={signupLoading || !isFormValid}
                className={`w-full ${isAccessible ? 'py-6 text-2xl' : 'py-5 text-xl'} bg-amber-600 hover:bg-amber-700 font-bold rounded-2xl inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:pointer-events-none kiosk-white-text mt-4`}
                data-testid="kiosk-signup-submit"
              >
                {signupLoading ? (
                  <Loader2 className={`${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} animate-spin`} />
                ) : (
                  <>
                    <Crown className={`${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} mr-2`} />
                    Join & Start Ordering
                  </>
                )}
              </button>

              <p className="text-center text-gray-500 text-xs mt-3">
                By joining, you agree to receive occasional promotional messages.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
